import { Box, Button, Icon, IconButton, Slider, Typography, useTheme } from "@mui/material";
import { useState, useEffect } from "react";
import { WorldRenderer } from "../../WorldRenderer";
import { WREvents } from "../../WREvents";
import { Vec2 } from "../../../models/SyntheticMap";
import Konva from "konva";
import { Stage, Layer, Circle, Line, Group, Text } from "react-konva";
import { FloorVisual } from "../../../visuals/FloorVisual";
import { AddBox, AddCircle, AlignHorizontalCenter, DoorBackOutlined, DoorSliding, VerticalAlignCenter } from "@mui/icons-material";

let _callback: (newFloor: FloorVisual) => void = () => { };
const TOOLBAR_HEIGHT = 80;

interface LineSegment {
  index: number;
  outer: boolean;
  startPoint: Vec2;
  endPoint: Vec2;
  inWallIndex?: number;
}

export function FloorEditor({ wr, size, position }: {
  wr: WorldRenderer,
  size: { width: number, height: number },
  position: { x: number, y: number }
}) {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [floor, setFloor] = useState<FloorVisual>();
  const [scale, setScale] = useState(100);
  const [selectedSegment, setSelectedSegment] = useState<LineSegment | null>(null);
  const [selectedNewPoint, setSelectedNewPoint] = useState<Vec2 | null>(null);

  useEffect(() => {
    const listenerForEditPolygon = (floor: FloorVisual, callback: (newFloor: FloorVisual) => void) => {
      setFloor({ ...floor } as FloorVisual);
      setScale(floor.scale || 100);
      setIsEditing(true);
      _callback = callback;
    };
    wr.on(WREvents.EditFloor, listenerForEditPolygon);
    return () => {
      wr.off(WREvents.EditFloor, listenerForEditPolygon);
    };
  }, [wr]);

  const center: Vec2 = {
    x: size.width / 2,
    y: (size.height / 2) - TOOLBAR_HEIGHT
  };

  function convertToKonvaPoints(points: Vec2[]): number[] {
    return points.flatMap(point => {
      const konvaPoint = convertToKonva(point);
      return [konvaPoint.x, konvaPoint.y];
    });
  }

  function convertToKonva(point: Vec2): Vec2 {
    return {
      x: Math.round(point.x * scale + center.x),
      y: Math.round(center.y - point.y * scale)
    };
  }

  function convertFromKonva(x: number, y: number): Vec2 {
    return {
      x: (x - center.x) / scale,
      y: (center.y - y) / scale
    };
  }

  if (!floor || !isEditing) {
    return null;
  }

  function findDistanceToLineFromPoint(point: Vec2, lineStart: Vec2, lineEnd: Vec2): number {
    // Vector from lineStart to lineEnd
    const dx = lineEnd.x - lineStart.x;
    const dy = lineEnd.y - lineStart.y;
    // Length of the line segment squared
    const lengthSquared = dx * dx + dy * dy;

    // Handle case where lineStart and lineEnd are the same point
    if (lengthSquared === 0) {
      return Math.sqrt(
        (point.x - lineStart.x) ** 2 + (point.y - lineStart.y) ** 2
      );
    }

    // Vector from lineStart to point
    const px = point.x - lineStart.x;
    const py = point.y - lineStart.y;

    // Projection of point onto the line (dot product / lengthSquared)
    const t = Math.max(0, Math.min(1, (px * dx + py * dy) / lengthSquared));

    // Closest point on the line segment
    const closestX = lineStart.x + t * dx;
    const closestY = lineStart.y + t * dy;

    // Distance from point to closest point on the segment
    return Math.sqrt(
      (point.x - closestX) ** 2 + (point.y - closestY) ** 2
    );
  }

  // Handle node drag to edit shape
  const handleOuterWallDrag = (index: number, e: Konva.KonvaEventObject<DragEvent>) => {
    const outerWallPoints = [...floor.outerWall];
    const pos = e.target.position();
    const newPos = convertFromKonva(pos.x, pos.y);
    // console.log(`Dragging point ${index} to new position:`, newPos);
    outerWallPoints[index] = newPos;
    setSelectedSegment(null);
    setFloor({ ...floor, outerWall: outerWallPoints } as FloorVisual);
  };

  const handleInnerWallDrag = (wallIndex: number, pointIndex: number, e: Konva.KonvaEventObject<DragEvent>) => {
    const innerWalls = [...floor.innerWalls];
    const pos = e.target.position();
    const newPos = convertFromKonva(pos.x, pos.y);
    innerWalls[wallIndex][pointIndex] = newPos;
    setSelectedSegment(null);
    setFloor({ ...floor, innerWalls } as FloorVisual);
  };

  function clickHandler(e: Konva.KonvaEventObject<MouseEvent>) {
    const outerWallPoints = floor!.outerWall;
    const clickedPoint = convertFromKonva(e.evt.offsetX, e.evt.offsetY);
    const threshold = 0.25; // pixels
    for (let i = 0; i < outerWallPoints.length; i++) {
      const point = outerWallPoints[i];
      const nextPoint = outerWallPoints[i === outerWallPoints.length - 1 ? 0 : i + 1];
      const lineDistance = findDistanceToLineFromPoint(clickedPoint, point, nextPoint);
      if (lineDistance < threshold) {
        setSelectedSegment({
          index: i,
          outer: true,
          startPoint: point,
          endPoint: nextPoint,
        });
        setSelectedNewPoint(null);
        return;
      }
    }
    for (let i = 0; i < floor!.innerWalls.length; i++) {
      const wall = floor!.innerWalls[i];
      for (let j = 0; j < wall.length - 1; j++) {
        const lineDistance = findDistanceToLineFromPoint(clickedPoint, wall[j], wall[j + 1]);
        if (lineDistance < threshold) {
          setSelectedSegment({
            index: i,
            outer: false,
            startPoint: wall[j],
            endPoint: wall[j + 1],
            inWallIndex: j
          });
          setSelectedNewPoint(null);
          return;
        }
      }
    }
    setSelectedSegment(null);
  }

  const outerWallPoints = convertToKonvaPoints(floor.outerWall);

  const gridHlines: number[] = [];
  for (let i = scale; i < center.y; i += scale) {
    gridHlines.push(center.y - i);
    gridHlines.push(center.y + i);
  }
  const gridVLines: number[] = [];
  for (let i = scale; i < center.x; i += scale) {
    gridVLines.push(center.x - i);
    gridVLines.push(center.x + i);
  }

  return (
    <div
      style={{
        position: "absolute",
        top: `${position.y}px`,
        left: `${position.x}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        pointerEvents: isEditing ? "all" : "none",
        backgroundColor: isEditing ? 'rgba(100, 100, 100, 0.5)' : 'transparent',
      }}
    >
      <Box sx={{
        backgroundColor: theme.palette.background.paper,
        padding: 1,
        display: 'flex',
        gap: theme.spacing(2),
        alignItems: 'center',
        justifyContent: 'flex-end'
      }}>
        {selectedNewPoint &&
          <IconButton
            size="small"
            color="secondary"
            onClick={() => {
              const newWall = [
                selectedNewPoint,
                { x: selectedNewPoint.x + 1, y: selectedNewPoint.y }
              ];
              setSelectedNewPoint(null);
              setFloor({ ...floor, innerWalls: [...floor.innerWalls, newWall] } as FloorVisual);
            }}
          >
            <AddBox />
          </IconButton>
        }
        {selectedSegment && (
          <>
            <IconButton
              size="small"
              color="secondary"
              onClick={() => {
                if (selectedSegment.outer) {
                  // Handle adding a new point to the outer wall
                  const newPoint = {
                    x: (selectedSegment.startPoint.x + selectedSegment.endPoint.x) / 2,
                    y: (selectedSegment.startPoint.y + selectedSegment.endPoint.y) / 2
                  };
                  const updatedOuterWall = [...floor.outerWall];
                  updatedOuterWall.splice(selectedSegment.index + 1, 0, newPoint);
                  setFloor({ ...floor, outerWall: updatedOuterWall } as FloorVisual);
                } else {
                  const newPoint = {
                    x: (selectedSegment.startPoint.x + selectedSegment.endPoint.x) / 2,
                    y: (selectedSegment.startPoint.y + selectedSegment.endPoint.y) / 2
                  };
                  let wall = floor.innerWalls[selectedSegment.index];
                  wall.splice(selectedSegment.inWallIndex! + 1, 0, newPoint);
                  setFloor({ ...floor, innerWalls: [...floor.innerWalls] } as FloorVisual);
                }
              }}
            >
              <AddCircle color="secondary" />
            </IconButton>
            <IconButton
              size="small"
              color="secondary"
              onClick={() => {
                if (selectedSegment.outer) {
                  // Handle aligning the outer wall points
                  const point1 = floor.outerWall[selectedSegment.index];
                  const point2 = floor.outerWall[selectedSegment.index + 1];
                  point2.y = point1.y; // Align the second point with the first
                  setFloor({ ...floor, outerWall: [...floor.outerWall] } as FloorVisual);
                } else {
                  const point1 = floor.innerWalls[selectedSegment.index][selectedSegment.inWallIndex!];
                  const point2 = floor.innerWalls[selectedSegment.index][selectedSegment.inWallIndex! + 1];
                  point2.y = point1.y; // Align the second point with the first
                  setFloor({ ...floor, innerWalls: [...floor.innerWalls] } as FloorVisual);
                }
              }}
            >
              <VerticalAlignCenter color="secondary" />
            </IconButton>
            <IconButton
              size="small"
              color="secondary"
              onClick={() => {
                if (selectedSegment.outer) {
                  // Handle aligning the outer wall points
                  const point1 = floor.outerWall[selectedSegment.index];
                  const nextPointIndex = selectedSegment.index === floor.outerWall.length - 1 ? selectedSegment.index : selectedSegment.index + 1;
                  const point2 = floor.outerWall[nextPointIndex];
                  point2.x = point1.x; // Align the second point with the first
                  setFloor({ ...floor, outerWall: [...floor.outerWall] } as FloorVisual);
                } else {
                  const point1 = floor.innerWalls[selectedSegment.index][selectedSegment.inWallIndex!];
                  const point2 = floor.innerWalls[selectedSegment.index][selectedSegment.inWallIndex! + 1];
                  point2.x = point1.x; // Align the second point with the first
                  setFloor({ ...floor, innerWalls: [...floor.innerWalls] } as FloorVisual);
                }
              }}
            >
              <AlignHorizontalCenter color="secondary" />
            </IconButton>

          </>
        )}

        {(selectedSegment && selectedSegment.outer) && (
          <IconButton
            size="small"
            color="secondary"
            onClick={() => {
              const index = selectedSegment.index;
              if (floor.doorIndexes.includes(index)) {
                setFloor({
                  ...floor,
                  doorIndexes: floor.doorIndexes.filter(i => i !== index)
                } as FloorVisual);
              } else {
                setFloor({
                  ...floor,
                  doorIndexes: [...floor.doorIndexes, index]
                } as FloorVisual);
              }
            }}
          >
            {floor.doorIndexes.includes(selectedSegment.index) ? (
              <DoorSliding color="secondary" />
            ) : (
              <DoorBackOutlined color="secondary" />
            )}
          </IconButton>
        )}


        <Typography variant="body2">Scale:</Typography>
        <Slider
          color="secondary"
          size="small"
          value={scale}
          onChange={(e, newValue) => {
            setScale(newValue as number);
            setFloor({ ...floor, scale: newValue as number } as FloorVisual);
          }}
          min={10}
          max={200}
          sx={{ width: 100 }}
        />
        <Button color='secondary' variant="contained" onClick={() => {
          if (isEditing) {
            setIsEditing(false);
            _callback(floor!);
          }
        }} >Done</Button>
      </Box>
      <Stage
        width={size.width}
        height={size.height - TOOLBAR_HEIGHT} // Adjust height to account for button
        style={{ border: '1px solid black' }}
        onClick={clickHandler}
      >
        <Layer>
          <Line points={[0, center.y, size.width, center.y]} stroke="red" strokeWidth={8} />
          <Line points={[center.x, 0, center.x, size.height]} stroke="green" strokeWidth={8} />
          {gridHlines.map(y => (
            <Line key={y} points={[0, y, center.x * 2, y]} stroke="lightgray" strokeWidth={1} />
          ))}
          {gridVLines.map(x => (
            <Line key={x} points={[x, 0, x, center.y * 2]} stroke="lightgray" strokeWidth={1} />
          ))}
        </Layer>
        <Layer>
          <Line
            points={outerWallPoints}
            stroke="black"
            strokeWidth={2}
            closed={outerWallPoints.length > 2}
            onClick={(e) => setSelectedNewPoint(convertFromKonva(e.evt.offsetX, e.evt.offsetY))}
            fill="rgba(200, 200, 200, 0.5)"
          />
          {floor.outerWall.map(convertToKonva).map((point, index) => (
            <Group
              key={index}
              x={point.x}
              y={point.y}
              onDragMove={(e) => handleOuterWallDrag(index, e)}
              draggable

            >
              <Text
                text={`[${index}]`}
                fontSize={16}
                x={10}
                y={-20}
                fill="red"
              />
              <Circle
                key={index}
                x={0}
                y={0}
                radius={8}
                fill="red"
              >
              </Circle>
            </Group>
          ))}
          {selectedNewPoint && (
            <Circle
              x={convertToKonva(selectedNewPoint).x}
              y={convertToKonva(selectedNewPoint).y}
              radius={10}
              fill="blue"
            />
          )}

          {floor.doorIndexes.map((index) => {
            const point = floor.outerWall[index];
            const nextPoint = floor.outerWall[(index + 1) % floor.outerWall.length];
            return (
              <Line
                key={index}
                points={[
                  convertToKonva(point).x,
                  convertToKonva(point).y,
                  convertToKonva(nextPoint).x,
                  convertToKonva(nextPoint).y
                ]}
                stroke="orange"
                strokeWidth={8}
              />
            );
          })}
          {floor.innerWalls.map((wall, wallIndex) => (
            <Line
              key={wallIndex}
              points={convertToKonvaPoints(wall)}
              stroke="green"
              strokeWidth={6}
            />
          ))}
          {floor.innerWalls.map((wall, wallIndex) => (
            <Group key={wallIndex}>
              {wall.map((point, pointIndex) => (
                <Circle
                  key={pointIndex}
                  x={convertToKonva(point).x}
                  y={convertToKonva(point).y}
                  radius={8}
                  fill={pointIndex === 0 ? "red" : "green"}
                  draggable
                  onDragMove={(e) => handleInnerWallDrag(wallIndex, pointIndex, e)}
                />
              ))}
            </Group>
          ))}
          {selectedSegment && (
            <Line
              points={[
                convertToKonva(selectedSegment.startPoint).x,
                convertToKonva(selectedSegment.startPoint).y,
                convertToKonva(selectedSegment.endPoint).x,
                convertToKonva(selectedSegment.endPoint).y
              ]}
              stroke="blue"
              strokeWidth={3}
            />
          )}
        </Layer>
      </Stage>
    </div>
  );
}